import { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Search, ChevronDown, MessageCircle, X } from 'lucide-react'
import posthog from 'posthog-js'
import {
  faqCategories,
  faqItems,
  type FaqCategoryId,
  type FaqItem,
} from '@/content/vendorFaq'

type CategoryFilter = FaqCategoryId | 'all'

const SUPPORT_EMAIL = 'support@tixxly.ai'

export default function HelpPage() {
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all')
  const [openId, setOpenId] = useState<string | null>(null)
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Hash deep-linking: /vendor/help#<faq-id> opens that FAQ; /vendor/help#<category-id>
  // selects that category. Also re-runs when the hash changes (e.g., user follows
  // a contextual link from the OnboardingPage).
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (!hash) return

    const matchingItem = faqItems.find((f) => f.id === hash)
    if (matchingItem) {
      setActiveCategory(matchingItem.category)
      setOpenId(matchingItem.id)
      requestAnimationFrame(() => {
        itemRefs.current[matchingItem.id]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
      return
    }

    const matchingCategory = faqCategories.find((c) => c.id === hash)
    if (matchingCategory) {
      setActiveCategory(matchingCategory.id)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [location.hash])

  const filtered = useMemo<FaqItem[]>(() => {
    const q = query.trim().toLowerCase()
    return faqItems.filter((item) => {
      if (activeCategory !== 'all' && item.category !== activeCategory) return false
      if (!q) return true
      return (
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q)
      )
    })
  }, [query, activeCategory])

  const toggle = (id: string) => {
    const next = openId === id ? null : id
    setOpenId(next)
    if (next) {
      const item = faqItems.find((f) => f.id === id)
      posthog.capture('vendor_faq_opened', {
        faq_id: id,
        category: item?.category,
      })
    }
  }

  const handleCategoryClick = (id: CategoryFilter) => {
    setActiveCategory(id)
    posthog.capture('vendor_faq_category_selected', { category: id })
  }

  return (
    <div style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            margin: '0 0 0.5rem',
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-gray-900)',
          }}
        >
          Help Center
        </h1>
        <p
          style={{
            margin: 0,
            color: 'var(--color-gray-500)',
            fontSize: 'var(--text-sm)',
          }}
        >
          Answers to the most common questions from Tixxly vendors. Can't find
          what you're looking for? <a
            href={`mailto:${SUPPORT_EMAIL}`}
            style={{ color: 'var(--color-tixxly-navy)', fontWeight: 'var(--font-weight-medium)' }}
          >
            Email us
          </a>.
        </p>
      </div>

      {/* Search */}
      <div
        style={{
          position: 'relative',
          marginBottom: '1.25rem',
        }}
      >
        <Search
          size={16}
          color="var(--color-gray-400)"
          style={{
            position: 'absolute',
            left: '0.875rem',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search FAQs..."
          aria-label="Search FAQs"
          style={{
            width: '100%',
            padding: '0.625rem 2.5rem 0.625rem 2.5rem',
            border: '1px solid var(--color-gray-300)',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontFamily: 'var(--font-sans)',
            background: 'var(--color-pure-white)',
            outline: 'none',
          }}
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            aria-label="Clear search"
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              padding: '0.375rem',
              cursor: 'pointer',
              color: 'var(--color-gray-400)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Category chips */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <CategoryChip
          label="All"
          isActive={activeCategory === 'all'}
          onClick={() => handleCategoryClick('all')}
        />
        {faqCategories.map((cat) => (
          <CategoryChip
            key={cat.id}
            label={cat.label}
            isActive={activeCategory === cat.id}
            onClick={() => handleCategoryClick(cat.id)}
          />
        ))}
      </div>

      {/* FAQ list */}
      {filtered.length === 0 ? (
        <div
          className="card"
          style={{
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--color-gray-500)',
          }}
        >
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>
            No results for "{query}".
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem' }}>
            Try a different search, or{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              style={{ color: 'var(--color-tixxly-navy)' }}
            >
              email support
            </a>
            .
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map((item) => (
            <FaqAccordion
              key={item.id}
              item={item}
              isOpen={openId === item.id}
              onToggle={() => toggle(item.id)}
              registerRef={(el) => {
                itemRefs.current[item.id] = el
              }}
            />
          ))}
        </div>
      )}

      {/* Still need help */}
      <div
        className="card"
        style={{
          marginTop: '2rem',
          padding: '1.5rem',
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          background: 'var(--color-gray-50)',
          border: '1px solid var(--color-gray-200)',
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--color-pure-white)',
            border: '1px solid var(--color-gray-200)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MessageCircle size={18} color="var(--color-tixxly-navy)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: '0 0 0.25rem',
              fontSize: '0.95rem',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-gray-900)',
            }}
          >
            Still need help?
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: '0.85rem',
              color: 'var(--color-gray-600)',
            }}
          >
            Email{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              style={{
                color: 'var(--color-tixxly-navy)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {SUPPORT_EMAIL}
            </a>{' '}
            and we'll get back to you within one business day.
          </p>
        </div>
      </div>
    </div>
  )
}

function CategoryChip({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={isActive}
      style={{
        padding: '0.4rem 0.875rem',
        borderRadius: '999px',
        border: `1px solid ${
          isActive ? 'var(--color-tixxly-navy)' : 'var(--color-gray-300)'
        }`,
        background: isActive ? 'var(--color-tixxly-navy)' : 'var(--color-pure-white)',
        color: isActive ? 'var(--color-pure-white)' : 'var(--color-gray-700)',
        fontSize: '0.8rem',
        fontWeight: 'var(--font-weight-medium)',
        fontFamily: 'var(--font-sans)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  )
}

function FaqAccordion({
  item,
  isOpen,
  onToggle,
  registerRef,
}: {
  item: FaqItem
  isOpen: boolean
  onToggle: () => void
  registerRef: (el: HTMLDivElement | null) => void
}) {
  return (
    <div
      ref={registerRef}
      id={`faq-${item.id}`}
      className="card"
      style={{
        padding: 0,
        overflow: 'hidden',
        border: `1px solid ${
          isOpen ? 'var(--color-gray-300)' : 'var(--color-gray-200)'
        }`,
      }}
    >
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{
          width: '100%',
          padding: '1rem 1.25rem',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          textAlign: 'left',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <span
          style={{
            fontSize: '0.95rem',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-gray-900)',
          }}
        >
          {item.question}
        </span>
        <ChevronDown
          size={18}
          color="var(--color-gray-500)"
          style={{
            flexShrink: 0,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </button>
      {isOpen && (
        <div
          style={{
            padding: '0 1.25rem 1.25rem',
            fontSize: '0.9rem',
            lineHeight: 'var(--leading-relaxed)',
            color: 'var(--color-gray-700)',
            whiteSpace: 'pre-line',
          }}
        >
          {item.answer}
        </div>
      )}
    </div>
  )
}
